import { Loading } from '@livecontrol/core-ui';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { useEventBrand } from '../../store';
import { useScheduledEvent } from '../event-details';
import { LeftPanel } from './left-panel';
import { RightPanel } from './right-panel';
import { buildWebplayerUrl } from './webplayer-url';

interface Props {
  eid: string;
}

/*
 * Two-column split: the event (player + general info) on the left, a tabbed
 * panel on the right. Below 1180px the columns collapse to a single stack.
 */
const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 0.85fr) 1.15fr;
  gap: 24px;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

export const EventLayout = ({ eid }: Props): ReactElement => {
  const { loading, scheduledEvent, refetch } = useScheduledEvent(eid);
  const setBrand = useEventBrand((state) => state.setBrand);

  // Publish the event's name/organization/stream link to the header; clear on leave.
  useEffect(() => {
    if (scheduledEvent) {
      setBrand({
        title: scheduledEvent.name,
        subtitle: scheduledEvent.location?.user?.organization_name ?? undefined,
        link: buildWebplayerUrl(scheduledEvent)
      });
    }

    return (): void => {
      setBrand(undefined);
    };
  }, [scheduledEvent, setBrand]);

  if (loading) {
    return <Loading isOldVersion />;
  }

  if (!scheduledEvent) {
    return <div className='text-white'>Could not load event details</div>;
  }

  return (
    <Split>
      <LeftPanel scheduledEvent={scheduledEvent} refetchEvent={refetch} />
      <RightPanel scheduledEvent={scheduledEvent} />
    </Split>
  );
};
